import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, ArrowRight, Mail, Check } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { INV_PORTAL_API } from "@/lib/investorPortalApi";
import { EMAIL_RE } from "@/lib/validation";

export default function InvestorLogin() {
  const { t, i18n } = useTranslation();
  const [email,     setEmail]     = useState("");
  const [touched,   setTouched]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [state,     setState]     = useState({ status: "idle", message: "" });

  const emailErr = !email.trim()
    ? t("invPortal.login.errorRequired") || "E-Mail ist erforderlich."
    : !EMAIL_RE.test(email.trim())
    ? t("invPortal.login.errorInvalid") || "Bitte eine gültige E-Mail-Adresse eingeben."
    : null;
  const showErr = (submitted || touched) && !!emailErr;

  const inputClass = showErr
    ? "w-full border border-red-400 bg-red-50 rounded-2xl pl-11 pr-5 py-3.5 focus:border-red-400 focus:outline-none transition-colors text-cocoa"
    : !!email.trim() && !emailErr
    ? "w-full border border-green-400 bg-green-50 rounded-2xl pl-11 pr-5 py-3.5 focus:border-green-500 focus:outline-none transition-colors text-cocoa"
    : "w-full bg-porcelain border border-sandBeige rounded-2xl pl-11 pr-5 py-3.5 focus:border-raspberry focus:outline-none transition-colors text-cocoa";

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (emailErr) return;
    if (state.status === "loading") return;
    setState({ status: "loading", message: "" });
    try {
      const lang = i18n.language?.startsWith("en") ? "en" : "de";
      const { data } = await axios.post(`${INV_PORTAL_API}/magic-link/request`, { email, language: lang });
      setState({ status: "sent", message: data?.message || "" });
    } catch (err) {
      const code = err?.response?.status;
      setState({
        status: "error",
        message: code === 429 ? t("invPortal.login.rateLimited") : t("invPortal.login.errorGeneric"),
      });
    }
  };

  return (
    <div className="min-h-screen bg-porcelain flex items-center justify-center p-5" data-testid="investor-portal-login">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="w-full max-w-md bg-white border border-sandBeige rounded-[2rem] shadow-soft p-8 sm:p-10"
      >
        <Link to="/" className="font-serif text-2xl lowercase tracking-tight text-cocoa block mb-6">
          womenintech<span className="text-raspberry">.life</span>
        </Link>
        <p className="wit-eyebrow text-raspberry mb-3">{t("invPortal.shell.subtitle")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-cocoa leading-[1.05]" data-testid="investor-login-headline">
          {t("invPortal.login.title")}
        </h1>
        <p className="mt-4 text-warmGrey leading-relaxed">{t("invPortal.login.lead")}</p>

        {state.status === "sent" ? (
          <div className="mt-7 rounded-2xl bg-skyMist/60 p-5 flex flex-col items-start gap-3" data-testid="investor-login-success">
            <div className="w-10 h-10 rounded-full bg-raspberry text-white flex items-center justify-center">
              <Check size={18} />
            </div>
            <p className="text-cocoa text-sm leading-relaxed">
              {state.message || t("invPortal.login.successHint")}
            </p>
            <p className="text-warmGrey text-xs">{t("invPortal.login.successHintSub")}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate className="mt-7 flex flex-col gap-4" data-testid="investor-login-form">
            <div className="flex flex-col gap-2 text-sm text-cocoa">
              <span className="font-medium">
                {t("invPortal.login.email")} <span className="text-raspberry">*</span>
              </span>
              <div className="relative">
                <Mail
                  size={16}
                  className={`absolute left-4 top-1/2 -translate-y-1/2 ${showErr ? "text-red-400" : "text-warmGrey"}`}
                  aria-hidden
                />
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder={t("invPortal.login.emailPlaceholder")}
                  className={inputClass}
                  data-testid="investor-login-email"
                />
              </div>
              {showErr && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle size={12} />{emailErr}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={state.status === "loading"}
              className="bg-raspberry hover:bg-coral disabled:opacity-60 text-white rounded-full px-7 py-3.5 font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="investor-login-submit"
            >
              {state.status === "loading" ? t("invPortal.login.submitting") : t("invPortal.login.submit")}
              {state.status !== "loading" && <ArrowRight size={16} />}
            </button>
            {state.status === "error" && (
              <p className="text-coral text-sm flex items-center gap-2" data-testid="investor-login-error">
                <AlertCircle size={14} />{state.message}
              </p>
            )}
            <p className="text-xs text-warmGrey text-center mt-2">{t("invPortal.login.passwordless")}</p>
          </form>
        )}
        <div className="mt-6 pt-6 border-t border-sandBeige flex flex-col gap-2">
          <Link to="/investors" className="text-sm text-warmGrey hover:text-raspberry transition-colors">
            {t("invPortal.login.notYet")}
          </Link>
          <Link to="/contact" className="text-sm text-warmGrey hover:text-raspberry transition-colors">
            {t("invPortal.login.forgotEmail")}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
